import * as React from 'react'
import Head from 'next/head'
import { NextSeo } from 'next-seo'
import { Text, Title } from '@mantine/core'
import { ParsedUrlQuery } from 'querystring'

import * as Types from 'declarations'
import { countryNameSlugToIsoCode, getDefaultImage } from 'lib/helper'
import * as RedisUtil from 'lib/redis'

import { withVersion } from 'lib/withVersion'
import PageTemplate from 'components/PageTemplate'
import GuideDirectoryList from 'components/GuideDirectoryList'
import { getCountryGuidePageRoute } from 'lib/display'

interface GuidesPageProps {
	countrySlug: string
	continent: string
	guideOverviews: Types.API.GuideCompanyOverview[]
	distinctCountries: string[]
	version: Types.VERSION
}

const CountryGuidesPage = ({
	countrySlug,
	continent,
	guideOverviews,
	version,
}: GuidesPageProps) => {
	const capitalisedCountrySlug =
		countrySlug.charAt(0).toUpperCase() + countrySlug.slice(1)

	const titleH1 = `${
		version === Types.VERSION.TREKKING
			? 'Trekking'
			: 'Mountaineering & Climbing'
	} Guides in ${capitalisedCountrySlug}`

	const seo = {
		title: `${titleH1} - ${
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks'
		}`,
		description:
			version === Types.VERSION.TREKKING
				? 'Browse a directory of trekking guides on Guided Trekking. Find experienced professionals offering guided treks and trips around the world.'
				: 'Browse a directory of mountaineering guides on Guided Peaks. Find experienced professionals offering guided climbs and expeditions around the world.',
		canonical: getCountryGuidePageRoute(
			version === Types.VERSION.PEAKS
				? Types.App.DataTypes.TrekOrClimb.CLIMB
				: Types.App.DataTypes.TrekOrClimb.TREK,
			continent,
			undefined,
			countrySlug,
		),
		images: [
			{
				url: getDefaultImage(version),
				type: 'image/jpeg',
			},
		],
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
	}

	return (
		<>
			<Head>
				<title>{seo.title}</title>
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.images,
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<PageTemplate>
				<Title order={1}>{titleH1}</Title>
				<div id="guides-page">
					<Text mt="sm" mb="lg">
						There are {guideOverviews.length} guides operating trips in{' '}
						{capitalisedCountrySlug}.
					</Text>
					<GuideDirectoryList
						guides={guideOverviews}
						highlightedCountryISOCodes={[]}
						highlightedTripIds={[]}
					/>
				</div>
			</PageTemplate>
		</>
	)
}

interface CountryGuidePageQuery extends ParsedUrlQuery {
	countries?: string
	continent?: string
}

export const getServerSideProps = withVersion(
	async (context: {
		version: Types.VERSION
		params?: CountryGuidePageQuery
	}) => {
		if (context.version === Types.VERSION.TREKKING) {
			return { notFound: true }
		}
		const {
			countries: country,
			continent,
		} = context.params as CountryGuidePageQuery

		const guideOverviews: Types.API.GuideCompanyOverview[] = await RedisUtil.Data.GuideDirectory.get(
			{ version: context.version },
		)

		const countryCodeFromSlug = countryNameSlugToIsoCode(country as string)
		if (countryCodeFromSlug === 'country-not-found') {
			return { notFound: true }
		}
		const countryCode = countryCodeFromSlug.toUpperCase()

		const countryFiltered = guideOverviews.filter((guide) =>
			guide.countries.includes(countryCode as string),
		)

		return {
			props: {
				countrySlug: country,
				continent,
				guideOverviews: countryFiltered,
				version: context?.version,
			},
		}
	},
)

export default CountryGuidesPage
